import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { RefreshCw, Search, FileText, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SEOTools() {
  const [submitting, setSubmitting] = useState(false);

  const submitToIndexNow = async () => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/admin/indexnow/submit`,
        { submit_all: true },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        toast.success(`✅ ${response.data.message}`);
      } else {
        toast.error(`❌ ${response.data.message}`);
      }
    } catch (error) {
      console.error('IndexNow error:', error);
      toast.error('Failed to submit to IndexNow');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">SEO Tools</h1>

        {/* IndexNow Card */}
        <Card className="p-8 mb-6">
          <div className="flex items-start space-x-4">
            <div className="bg-blue-100 rounded-lg p-3">
              <Search className="w-8 h-8 text-blue-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">IndexNow - Instant Indexing</h2>
              <p className="text-gray-600 mb-4">
                Submit your site's URLs to Bing, Yandex, and other search engines for instant indexing.
                This bypasses the normal crawling process and gets your pages indexed within minutes.
              </p>
              
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4">
                <h3 className="font-semibold text-blue-900 mb-2">What gets submitted:</h3>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>✅ Homepage</li>
                  <li>✅ All active listings (up to 1,000)</li>
                  <li>✅ All blog posts</li>
                  <li>✅ Static pages (search, register, resources, etc.)</li>
                  <li>✅ Press releases</li>
                  <li>✅ Video gallery</li>
                </ul>
              </div>

              <Button
                onClick={submitToIndexNow}
                disabled={submitting}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {submitting ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Submitting to IndexNow...
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-2" />
                    Submit All URLs to IndexNow
                  </>
                )}
              </Button>
            </div>
          </div>
        </Card>

        {/* Sitemap Card */}
        <Card className="p-8 mb-6">
          <div className="flex items-start space-x-4">
            <div className="bg-green-100 rounded-lg p-3">
              <FileText className="w-8 h-8 text-green-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Sitemap</h2>
              <p className="text-gray-600 mb-4">
                Your sitemap is automatically generated and updated. Submit it to Google Search Console and Bing Webmaster Tools.
              </p>
              
              <div className="bg-gray-100 border border-gray-300 rounded p-3 mb-4">
                <code className="text-sm text-gray-800">https://its-sold.com/sitemap.xml</code>
              </div>

              <div className="space-y-2">
                <a
                  href="https://its-sold.com/sitemap.xml"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block"
                >
                  <Button variant="outline" className="mr-2">
                    <FileText className="w-4 h-4 mr-2" />
                    View Sitemap
                  </Button>
                </a>

                <a
                  href="https://search.google.com/search-console"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block"
                >
                  <Button variant="outline" className="mr-2">
                    Google Search Console
                  </Button>
                </a>

                <a
                  href="https://www.bing.com/webmasters"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block"
                >
                  <Button variant="outline">
                    Bing Webmaster Tools
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </Card>

        {/* Info Card */}
        <Card className="p-6 bg-green-50 border-green-200">
          <div className="flex items-start space-x-3">
            <CheckCircle className="w-6 h-6 text-green-600 mt-1" />
            <div>
              <h3 className="font-semibold text-green-900 mb-1">When to use IndexNow</h3>
              <ul className="text-sm text-green-800 space-y-1">
                <li>• After adding new listings or content</li>
                <li>• After updating important pages</li>
                <li>• Once per day maximum (don't spam)</li>
                <li>• After fixing sitemap errors</li>
              </ul>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
